import { ColDef } from "ag-grid-community";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./LeftMenu.css";
import { StyleSettings } from "../../styles/StyleSettings";
import { global, useMenuStore } from "@shared/libs/Global";
import { Menu } from "@shared/type/CommonType";
import { UseApiService } from "@shared/hooks/useApiService";
import { showDialog } from "@widgets/CommonDialog";

interface LeftMenuProps {
  width: number;
  onMenuClick: (
    menuKey: string,
    menuTitle: string,
    cd_module: string,
    menuContents: string,
    menuData: any,
    menuPath: string
  ) => void;
}

const getFullPath = (menuItems: any[]) => {
  if (menuItems) {
    for (let menuItem of menuItems) {
      // cd_menu_parent가 SN_REC 또는 SN_FAV이면 skip
      if (
        menuItem.cd_menu_parent === "SN_REC" ||
        menuItem.cd_menu_parent === "SN_FAV"
      ) {
        continue;
      }

      if (menuItem.fg_menu !== "MGM") {
        if (Array.isArray(menuItem.path_arr)) {
          const nm_path = menuItem.path_arr.map((code: string) => {
            const menu = menuItems.find((item) => item.cd_menu === code);
            return menu ? menu.nm_menu : "";
          });
          menuItem.nm_path = nm_path.join(" > ");
        } else {
          // path_arr이 없는 경우 대비
          menuItem.nm_path = "";
        }
      }
    }
  }
  return menuItems;
};

// 메뉴 트리 셀 렌더러.
// 컴포넌트 안에서 정의하면 LeftMenu가 리렌더될 때마다 '다른 컴포넌트'가 되어
// AG Grid가 모든 그룹 셀을 파기·재생성한다. 파기 과정에서 AG Grid 내부가
// setState를 호출하기 때문에(setCheckboxSpacing) 리렌더가 꼬리를 물고
// 'Maximum update depth exceeded'로 이어진다. 그래서 모듈 스코프에 고정한다.
const CustomGroupCellRenderer = (props: any) => {
  const spanRef = useRef<HTMLSpanElement>(null);

  // 렌더링 후 length 비교
  useEffect(() => {
    const checkLength = () => {
      if (spanRef.current) {
        const isEllipsis =
          spanRef.current.offsetWidth < spanRef.current.scrollWidth;
        // 툴팁 설정
        spanRef.current.title = isEllipsis ? props.value : "";
      }
    };

    // 컴포넌트가 처음 렌더링되었을 때 체크
    checkLength();
  }, [props.value]);

  if (props && props.data?.fg_menu === "MGM") {
    // data에서 icon 정보 찾아서 경로 만들기
    let iconUrl = "/images/opened_folder.svg";
    if (props.data.cd_menu === "SN_FAV") {
      iconUrl = "/images/bookmark_on.svg";
    } else if (props.data.cd_menu === "SN_REC") {
      iconUrl = "/images/opened_folder.svg";
    } else if (props.data.cd_module && props.data.no_level == 1) {
      iconUrl = `/images/folder_${props.data.cd_module.toLowerCase()}.svg`;
    }
    return (
      <span
        ref={spanRef}
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <img
          src={iconUrl}
          className="folder-img"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = "/images/opened_folder.svg";
          }}
        />
        {props.value}
      </span>
    );
  } else {
    return (
      <span
        ref={spanRef}
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {props.value}
      </span>
    );
  }
};

const LeftMenu = forwardRef(({ width, onMenuClick }: LeftMenuProps, ref) => {
  const { userInfo } = global();
  const { menuItems, setMenuItems, expandedMap, setExpandedMap } =
    useMenuStore();
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact | null>(null);
  const [gridApi, setGridApi] = useState<any>(null);

  const { apiPost } = UseApiService();

  // 스크롤 위치 관련
  const wrapperRef = useRef<HTMLDivElement>(null);
  const savedScrollTop = useRef(0);

  useImperativeHandle(ref, () => ({
    setColumnWidths: () => setColumnWidths,
  }));

  useEffect(() => {
    if (!userInfo) return;
    (async () => {
      await apiPost("/system/menus", {}, (response) => {
        if (response.code === 200) {
          const menu = response.data.map((item: any) => ({
            ...item,
            path_arr: item.path_arr ? item.path_arr.split(",") : [],
            path_origin: item.path_origin ? item.path_origin.split(",") : [],
          }));
          setMenuItems(menu);
        } else if (response.code !== 498) {
          showDialog("error", "메뉴를 가져오는데 실패했습니다.");
        }
      });
    })();
  }, [userInfo, navigate]);

  useEffect(() => {
    if (!gridApi) return;

    const entries = Object.entries(expandedMap); // { [cd_menu]: boolean }
    for (const [id, expanded] of entries) {
      const node = gridApi.getRowNode(id);
      if (node && node.expanded !== expanded) node.setExpanded(expanded);
    }
  }, [menuItems, gridApi]);

  const columnDefs: ColDef<Menu>[] = useMemo(
    () => [
      {
        headerName: "cd_menu",
        field: "cd_menu",
        hide: true,
      },
      {
        headerName: "nm_menu",
        field: "nm_menu",
        hide: true,
      },
      {
        headerName: "cd_module",
        field: "cd_module",
        hide: true,
      },
      {
        headerName: "nm_file",
        field: "nm_file",
        hide: true,
      },
    ],
    []
  );

  // 셀 단일클릭 이벤트 핸들러
  const handleCellClicked = useCallback((e: any) => {
    const { node } = e;
    if (node.group && node.data?.cd_menu) {
      node.setExpanded(!node.expanded); // ← 상태 저장 X (onRowGroupOpened가 저장)
    }
  }, []);

  // 아이템 클릭 처리 함수
  const handleItemClick = (e: any) => {
    if (e && e.itemData) {
      const { nm_menu, nm_file, cd_menu, fg_menu, cd_module } = e.itemData;
      console.log("폴더 메뉴 선택:", e.itemData);

      // 폴더 메뉴(MGM)는 더블 클릭 무시
      if (fg_menu === "MGM") {
        console.log("폴더 메뉴 선택:", e.itemData);
        return;
      }

      const menus = getFullPath(menuItems);
      const matchedItem = menus.find(
        (item) =>
          item.cd_menu === cd_menu &&
          item.cd_menu_parent !== "SN_REC" &&
          item.cd_menu_parent !== "SN_FAV"
      );
      const nm_path = matchedItem ? matchedItem.nm_path : "";

      if (nm_menu && nm_file && cd_menu && cd_module && nm_path) {
        console.log("Double-clicked Menu Item:", e.itemData);
        const filePath = `./${cd_module}/${nm_file}/${nm_file}`.toUpperCase();
        onMenuClick(cd_menu, nm_menu, cd_module, filePath, e.itemData, nm_path);
      } else {
        alert("등록된 메뉴가 없습니다..");
      }
    } else {
      console.error("Invalid menu item data:", e);
    }
  };

  // AG Grid에 넘기는 객체/함수는 매 렌더마다 새로 만들면 안 된다.
  // 새 값으로 인식되면 그리드가 컬럼과 셀을 통째로 다시 만들고,
  // 그 파기 과정에서 AG Grid 내부 setState가 연쇄되어 업데이트 한도를 넘긴다.
  const autoGroupColumnDef = useMemo(
    () => ({
      headerName: "Menu", // 그룹 컬럼의 헤더 이름
      field: "nm_menu", // 데이터를 표시할 필드
      width: width, // 동적으로 너비 설정
      cellRendererParams: {
        suppressCount: true, // 하위 아이템 개수 숨기기
        suppressDoubleClickExpand: true, // 더블 클릭으로 확장/축소 비활성화
        suppressHoverHighlight: false,
        innerRenderer: CustomGroupCellRenderer,
      },
    }),
    [width]
  );

  const rowStyle = useMemo(
    () => ({ fontSize: StyleSettings.font + 1 }),
    [StyleSettings.font]
  );

  const getDataPath = useCallback((data: any) => data.path_arr || [], []);

  const getRowId = useCallback(
    (p: any) => `${p.data.cd_menu_parent}-${p.data.cd_menu}`,
    []
  );

  // 그룹 컬럼의 너비 동적 변경
  const setColumnWidths = () => {
    if (gridRef.current?.api) {
      // 컬럼 너비 변경
      gridRef.current.api.setColumnWidths([
        { key: "nm_menu", newWidth: width },
      ]);
    }
  };

  const getViewportEl = () =>
    wrapperRef.current?.querySelector(
      ".ag-body-viewport"
    ) as HTMLDivElement | null;

  const saveScroll = () => {
    const vp = getViewportEl();
    if (vp) savedScrollTop.current = vp.scrollTop;
  };

  const restoreScroll = () => {
    const vp = getViewportEl();
    if (!vp) return;
    // 스크롤 범위 넘지 않도록 클램프
    const max = Math.max(0, vp.scrollHeight - vp.clientHeight);
    vp.scrollTop = Math.min(savedScrollTop.current, max);
  };

  // 펼침/접힘 직후에도 스크롤 복원 (토글 시 튐 방지)
  const onRowGroupOpenedImpl = (e: any) => {
    const key = e.node?.data?.cd_menu;
    if (key) setExpandedMap({ ...expandedMap, [key]: e.node.expanded });
    requestAnimationFrame(restoreScroll);
  };

  // 그리드에 넘기는 핸들러는 identity가 바뀌면 안 된다(바뀌면 셀을 다시 만든다).
  // 최신 클로저는 ref로 통과시켜서, 콜백을 새로 만들지 않고도 현재 상태를 본다.
  const latestRef = useRef({ handleItemClick, onRowGroupOpenedImpl });
  latestRef.current = { handleItemClick, onRowGroupOpenedImpl };

  const handleDoubleClicked = useCallback((e: any) => {
    latestRef.current.handleItemClick({ itemData: e.data });
  }, []);

  const handleRowGroupOpened = useCallback((e: any) => {
    latestRef.current.onRowGroupOpenedImpl(e);
  }, []);

  const handleGridReady = useCallback(
    (params: any) => setGridApi(params.api),
    []
  );

  return (
    <div className="w-full h-full leftMenu-wrapper">
      <div className={`ag-theme-alpine w-full h-full`}>
        <AgGridReact
          rowStyle={rowStyle}
          ref={gridRef}
          rowData={menuItems}
          columnDefs={columnDefs}
          autoGroupColumnDef={autoGroupColumnDef} // 그룹 컬럼 정의
          treeData={true} // 트리 구조 활성화
          getDataPath={getDataPath} // 트리 데이터 경로 정의
          onCellClicked={handleCellClicked} // 셀 클릭 핸들러
          onCellDoubleClicked={handleDoubleClicked} // 셀 더블 클릭 핸들러
          onRowGroupOpened={handleRowGroupOpened}
          animateRows={false} // 애니메이션 활성화
          headerHeight={0} // 헤더 숨기기
          suppressContextMenu={true} // 우클릭 기능 비활성화
          onGridReady={handleGridReady}
          getRowId={getRowId} // 각 행의 고유 ID 지정
        />
      </div>
    </div>
  );
});

export default LeftMenu;
